<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  NForm, NFormItem, NInput, NButton, NRate, NCard,
  NSpin, NAlert, NSpace, NInputNumber, NTag,
  NGrid, NGridItem, NDivider
} from 'naive-ui';
import { getProvider } from '@/services/provider';
import {
  createReview, updateReview, fetchMyReviewForProvider, fetchProviderReviews, hasReviewedProvider
} from '@/services/review.js';
import { authToken, isAuthenticated } from "@/services/auth";

const route = useRoute();
const router = useRouter();

const providerId = computed(() => route.params.id);
const reviewId = computed(() => {
  // This will ensure we get the reviewId parameter
  const id = route.params.reviewId;
  return id;
});

const isEditMode = computed(() => {
  // Check for reviewId and that it's not 'create'
  const isEdit = !!reviewId.value && reviewId.value !== 'create';
  return isEdit;
});


// Page state
const loading = ref(true);
const submitting = ref(false);
const error = ref(null);
const success = ref(false);
const provider = ref(null);
const existingReview = ref(null);

// Form model
const formModel = ref({
  rating: 0,
  reviewText: '',
  communicationRating: null,
  knowledgeRating: null,
  timelinessRating: null
});

// Form rules
const rules = {
  rating: [
    { required: true, message: 'Please provide an overall rating', trigger: 'change' }
  ],
  reviewText: [
    { max: 1000, message: 'Review text must be less than 1000 characters', trigger: 'blur' }
  ]
};

// Load provider details and check if user has an existing review
const loadData = async () => {
  loading.value = true;
  error.value = null;

  try {
    // Get the provider ID from the route params
    const provId = route.params.id;
        
    if (!provId) {
      throw new Error('Provider ID is missing from the URL');
    }
    
    // Load provider data first
    const language = localStorage.getItem('preferredLanguage') || 'english';
    provider.value = await getProvider(provId, language);
    
    if (!provider.value) {
      throw new Error('Provider not found');
    }
    
    // Now handle the review data based on whether we're in edit mode
    if (isEditMode.value) {
      // In edit mode, we need to specifically fetch the review by ID
      const reviewId = route.params.reviewId;
      
      // IMPORTANT: If fetchMyReviewForProvider doesn't accept a review ID parameter,
      // you might need to use a different API call here to get a specific review
      
      // This might be the issue - it looks like you're trying to get "my review for provider"
      // but if you need a specific review by ID, you might need a different API call
      
      try {
        // Try first getting the review specifically by ID if you have such a method
        // const myReview = await fetchReviewById(authToken.value, reviewId);
        
        // Or if you don't have such a method, get "my review" for this provider
        const myReview = await fetchMyReviewForProvider(authToken.value, provId);
        
        if (!myReview) {
          throw new Error('Review not found');
        }
        
        existingReview.value = myReview;
        
        // Populate form with existing review data
        formModel.value = {
          rating: myReview.rating || 0,
          reviewText: myReview.review_text || '',
          communicationRating: myReview.communication_rating || null,
          knowledgeRating: myReview.knowledge_rating || null,
          timelinessRating: myReview.timeliness_rating || null
        };
        
      } catch (err) {
        throw new Error(`Could not find your review: ${err.message}`);
      }
    } else {
      // In create mode, check if user already has a review
      try {
        const myReview = await fetchMyReviewForProvider(authToken.value, provId);
        
        if (myReview) {
          // User already has a review - redirect to edit page
          router.replace(`/providers/${provId}/review/${myReview.id}`);
          return;
        }
      } catch (err) {
        // If it's a 404, that's expected - user hasn't reviewed yet
        if (err.response && err.response.status === 404) {
        } else {
          throw err; // Re-throw unexpected errors
        }
      }
    }
  } catch (err) {
    console.error('Error loading data:', err);
    error.value = `Failed to load data: ${err.message}`;
  } finally {
    loading.value = false;
  }
};

// Handle form submission
const handleSubmit = async () => {
  submitting.value = true;
  error.value = null;
  success.value = false;

  try {
    const reviewData = {
      rating: formModel.value.rating,
      reviewText: formModel.value.reviewText,
      additionalRatings: {
        communicationRating: formModel.value.communicationRating,
        knowledgeRating: formModel.value.knowledgeRating,
        timelinessRating: formModel.value.timelinessRating
      }
    };

    if (isEditMode.value) {
      // Update existing review
      await updateReview(
        authToken.value,
        existingReview.value.id,
        reviewData.rating,
        reviewData.reviewText,
        reviewData.additionalRatings
      );
    } else {
      // Create new review
      await createReview(
        authToken.value,
        providerId.value,
        reviewData.rating,
        reviewData.reviewText,
        reviewData.additionalRatings
      );
    }

    success.value = true;

    // Redirect to provider page after a short delay
    setTimeout(() => {
      router.push(`/providers/${providerId.value}`);
    }, 1500);
  } catch (err) {
    console.error('Error submitting review:', err);
    error.value = `Failed to submit review: ${err.message}`;
  } finally {
    submitting.value = false;
  }
};

// Helper function to fetch all user reviews
const fetchMyReviews = async (token) => {
  return await fetchMyReviews(token);
};

// Navigation
const goBack = () => {
  router.back();
};

onMounted(() => {
  // Redirect if not authenticated
  if (!isAuthenticated) {
    router.push('/login');
    return;
  }

  loadData();
});
</script>

<template>
  <div class="review-page">
    <main class="main-content">
      <div class="container">
        <!-- Back Button -->
        <div class="back-section">
          <NButton @click="goBack" class="back-btn">
            ← Back
          </NButton>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-section">
          <NSpin size="large">
            <div class="loading-text">Loading...</div>
          </NSpin>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-section">
          <NAlert type="error" title="Error" closable>
            {{ error }}
            <template #action>
              <NButton size="small" @click="loadData">
                Try Again
              </NButton>
            </template>
          </NAlert>
        </div>

        <!-- Success Message -->
        <div v-else-if="success" class="success-section">
          <NAlert type="success" title="Success!" closable>
            <p v-if="isEditMode">Your review has been updated successfully!</p>
            <p v-else>Your review has been submitted successfully!</p>
            <p>Redirecting to provider page...</p>
          </NAlert>
        </div>

        <!-- Review Form -->
        <div v-else-if="provider" class="review-form-section">
          <h1 class="page-title">
            {{ isEditMode ? 'Edit Review' : 'Write a Review' }}
          </h1>

          <!-- Provider Info Card -->
          <NCard class="provider-card">
            <div class="provider-info">
              <h2 class="provider-name">{{ provider.provider_name }}</h2>
              <div class="provider-details">
                <div v-if="provider.specializations?.length" class="specializations">
                  <NTag v-for="spec in provider.specializations" :key="spec" type="info" size="small" class="spec-tag">
                    {{ spec }}
                  </NTag>
                </div>
                <div v-if="provider.city" class="location">
                  📍 {{ provider.city }}
                </div>
              </div>
            </div>
          </NCard>

          <!-- Review Form -->
          <NCard class="form-card">
            <NForm ref="formRef" :model="formModel" :rules="rules" label-placement="top" @submit.prevent="handleSubmit">
              <!-- Overall Rating -->
              <NFormItem label="Overall Rating" path="rating" required>
                <NRate v-model:value="formModel.rating" :count="5" />
                <div class="rating-help-text">
                  {{ formModel.rating > 0 ? `${formModel.rating} out of 5 stars` : 'Select your rating' }}
                </div>
              </NFormItem>

              <!-- Detailed Ratings -->
              <div class="detailed-ratings-section">
                <h3 class="section-title">Detailed Ratings</h3>
                <p class="section-help">These ratings are optional but help provide more specific feedback</p>

                <NGrid :cols="1" :x-gap="12" :y-gap="16" :item-responsive="true">
                  <!-- Communication Rating -->
                  <NGridItem span="1">
                    <NFormItem label="Communication" path="communicationRating">
                      <div class="rating-row">
                        <NRate v-model:value="formModel.communicationRating" :count="5" />
                        <div class="rating-description">
                          How well does the provider communicate and explain medical information?
                        </div>
                      </div>
                    </NFormItem>
                  </NGridItem>

                  <!-- Knowledge Rating -->
                  <NGridItem span="1">
                    <NFormItem label="Knowledge & Expertise" path="knowledgeRating">
                      <div class="rating-row">
                        <NRate v-model:value="formModel.knowledgeRating" :count="5" />
                        <div class="rating-description">
                          How knowledgeable and experienced does the provider seem in their field?
                        </div>
                      </div>
                    </NFormItem>
                  </NGridItem>

                  <!-- Timeliness Rating -->
                  <NGridItem span="1">
                    <NFormItem label="Timeliness & Availability" path="timelinessRating">
                      <div class="rating-row">
                        <NRate v-model:value="formModel.timelinessRating" :count="5" />
                        <div class="rating-description">
                          How satisfied are you with the provider's availability and punctuality?
                        </div>
                      </div>
                    </NFormItem>
                  </NGridItem>
                </NGrid>
              </div>

              <NDivider />

              <!-- Review Text -->
              <NFormItem label="Your Review" path="reviewText">
                <NInput v-model:value="formModel.reviewText" type="textarea"
                  placeholder="Share your experience with this provider..." :autosize="{ minRows: 4, maxRows: 10 }"
                  :maxlength="1000" show-count />
              </NFormItem>

              <!-- Submit Button -->
              <div class="form-actions">
                <NButton type="primary" @click="handleSubmit" :loading="submitting" :disabled="formModel.rating === 0">
                  {{ isEditMode ? 'Update Review' : 'Submit Review' }}
                </NButton>
              </div>
            </NForm>
          </NCard>
        </div>

        <!-- Provider Not Found -->
        <div v-else class="not-found">
          <h2>Provider Not Found</h2>
          <p>The provider you're trying to review doesn't exist.</p>
          <NButton type="primary" @click="goBack">
            Go Back
          </NButton>
        </div>
      </div>
    </main>
  </div>
</template>

<style scoped>
.review-page {
  min-height: 100vh;
  background-color: #f9fafb;
  padding-bottom: 2rem;
}

.main-content {
  padding-top: 1rem;
}

.container {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1rem;
}

.back-section {
  margin-bottom: 1.5rem;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  font-weight: 500;
}

.loading-section,
.error-section,
.success-section,
.not-found {
  text-align: center;
  padding: 3rem 0;
}

.loading-text {
  margin-top: 1rem;
  color: #6b7280;
}

.page-title {
  font-size: 1.875rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 1.5rem;
  text-align: center;
}

.provider-card {
  margin-bottom: 1.5rem;
}

.provider-info {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.provider-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
  text-align: center;
}

.provider-details {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.specializations {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.location {
  color: #6b7280;
  font-size: 0.875rem;
}

.form-card {
  margin-bottom: 2rem;
}

.rating-help-text {
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.5rem;
}

.detailed-ratings-section {
  margin: 1.5rem 0;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
}

.section-help {
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 1rem;
}

.rating-row {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.rating-description {
  font-size: 0.875rem;
  color: #6b7280;
}

.form-actions {
  margin-top: 1.5rem;
  display: flex;
  justify-content: center;
}

@media (min-width: 640px) {
  .form-actions {
    justify-content: flex-end;
  }
}

.not-found {
  text-align: center;
  color: #6b7280;
}

.not-found h2 {
  margin-bottom: 1rem;
  color: #1f2937;
}

.not-found p {
  margin-bottom: 1.5rem;
}
</style>